import React, { useEffect, useState, useRef } from 'react';
import { getBookingHubById, getBookingHubLocationsById } from '../../api';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setHubDetailsCache, setLocationsCache } from '../../redux/bookingHub';
import Icon, { SearchOutlined, FilterOutlined, GlobalOutlined, EnvironmentOutlined, CheckCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import '../../assets/css/bookingHub.css';
import LocationCard from './components/LocationCard';
import sorry from "../../assets/imgs/sorry.svg";

const BookingHub = ({ match }) => {
  const { gid } = match.params;
  const history = useHistory();
  const dispatch = useDispatch();

  const { hubDetailsByGid, locationsByGid } = useSelector(state => state.bookingHub);

  const [hubDetails, setHubDetails] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [selectedQuickBooking, setSelectedQuickBooking] = useState(0);

  const tagsContainerRef = useRef(null);

  const scrollTags = (direction) => {
    if (tagsContainerRef.current) {
      const scrollAmount = 150;
      tagsContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    // Clear all booking hub session state when returning to the landing page
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('bh-') || key === 'seatsio') {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Check and load details
      if (hubDetailsByGid[gid]) {
        setHubDetails(hubDetailsByGid[gid]);
      } else {
        try {
          const details = await getBookingHubById(gid);
          setHubDetails(details);
          dispatch(setHubDetailsCache({ gid, details }));
        } catch (error) {
          console.error("Error fetching booking hub details", error);
        }
      }

      // Check and load locations
      if (locationsByGid[gid]) {
        const activeLocs = (locationsByGid[gid] || []).filter(loc => loc.active === true);
        setLocations(activeLocs);
        setFilteredLocations(activeLocs);
      } else {
        try {
          const locsEntity = await getBookingHubLocationsById(gid);
          const locsList = (locsEntity?.bookingHubLocations || locsEntity?.content || []).filter(loc => loc.active === true);
          setLocations(locsList);
          setFilteredLocations(locsList);
          dispatch(setLocationsCache({ gid, locations: locsList }));
        } catch (error) {
          console.error("Error fetching booking hub locations", error);
          setLocations([]);
          setFilteredLocations([]);
        }
      }

      setLoading(false);
    };
    fetchData();
  }, [gid, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prepend "All" to the API tags for the filter bar
  const allTags = hubDetails?.tags
    ? [{ gid: '__all__', name: 'All' }, ...hubDetails.tags]
    : [];

  useEffect(() => {
    if (hubDetails?.sliders?.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % hubDetails.sliders.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [hubDetails]);

  // Filter by search query AND active tags
  useEffect(() => {
    let filtered = locations;

    if (activeTags.length > 0) {
      filtered = filtered.filter(loc => {
        const locTags = loc.tags || [];
        // Match location if it has ANY of the selected activeTags
        return locTags.some(t => {
          const name = typeof t === 'string' ? t : t?.name;
          if (!name) return false;
          return activeTags.some(at => at.toLowerCase() === name.toLowerCase());
        });
      });
    }

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(loc =>
        (loc.name || '').toLowerCase().includes(lowerQuery) ||
        (loc.description || '').toLowerCase().includes(lowerQuery) ||
        (loc.landmark || '').toLowerCase().includes(lowerQuery)
      );
    }

    setFilteredLocations(filtered);
    setSelectedQuickBooking(0);
  }, [searchQuery, activeTags, locations]);

  if (loading) return <div>Loading...</div>;
  if (!hubDetails) return <div>Hub not found</div>;

  if (hubDetails.active === false) {
    return (
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#F9FAFB',
          padding: '24px',
          fontFamily: 'Inter, sans-serif'
        }}
      >
        <div 
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '40px 32px',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
            textAlign: 'center',
            maxWidth: '440px',
            width: '100%',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px'
          }}
        >
          <img 
            src={sorry} 
            alt="Sorry" 
            style={{
              width: '160px',
              height: 'auto',
              display: 'block'
            }} 
          />
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 }}>
            Booking Temporarily Closed
          </h2>
          <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
            Sorry, bookings for this booking portal are currently closed. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-hub-container">
      <header className="booking-hub-header">
        <div className="booking-hub-header-left">
          {hubDetails.logo && <img className="booking-hub-logo" src={hubDetails.logo} alt="Logo" />}
        </div>
        <div className="booking-hub-header-center">
          <div className="booking-hub-header-title" style={{ fontSize: '13px', fontWeight: '800', letterSpacing: '1px', marginBottom: '0px' }}>
            {hubDetails.header || 'HERITAGE SITE BOOKING PORTAL'}
          </div>
          <p className="booking-hub-header-subtitle" style={{ fontSize: '10px', color: '#888' }}>
            {hubDetails.subHeader || 'Government of Tamil Nadu - Department of Archaeology'}
          </p>
        </div>
      </header>

      <div className="booking-hub-hero-section">
        {hubDetails.sliders && hubDetails.sliders.length > 0 && (
          <div className="booking-hub-carousel">
            {hubDetails.sliders.map((slide, index) => (
              <div
                key={index}
                className={`booking-hub-slide ${index === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url(${slide.imageUrl})` }}
              >
                <div className="booking-hub-overlay" />
                  <div className="booking-hub-slide-content">
                    {slide.label && (
                      <span className="booking-hub-slide-label" style={{ color: hubDetails.colorCode || '#C9822A' }}>
                        <span className="dot" style={{ backgroundColor: hubDetails.colorCode || '#C9822A' }}></span> {slide.label}
                      </span>
                    )}
                    <div className="booking-hub-slide-title">
                      {slide.primaryText} <br />
                      <span style={{ color: hubDetails.colorCode || '#C9822A' }}>{slide.primaryThemedText}</span>
                    </div>
                    <p className="booking-hub-slide-desc">{slide.secondaryText}</p>

                    <div className="booking-hub-slide-indicators">
                      {hubDetails.sliders.map((_, i) => (
                        <div
                          key={i}
                          className={`booking-hub-slide-dot ${i === currentSlide ? 'active' : ''}`}
                          style={{
                            backgroundColor: i === currentSlide ? (hubDetails.colorCode || '#C9822A') : '#d1bba4'
                          }}
                          onClick={() => setCurrentSlide(i)}
                        ></div>
                      ))}
                    </div>
                  </div>
              </div>
            ))}
          </div>
        )}

        {/* FLOATING QUICK BOOKING OVERLAY */}
        {locations.length > 0  && hubDetails?.enableQuickBooking && (
          <div className="booking-hub-quick-booking-card">
            <div className="booking-hub-quick-booking-header">
              <div className="booking-hub-quick-booking-title">QUICK BOOKING</div>
              <span className="booking-hub-status-available"><span className="dot"></span> Available</span>
            </div>
            <div className="booking-hub-quick-booking-label">SELECT SITE</div>
            <div className="booking-hub-quick-booking-list">
              {filteredLocations.length > 0 ? (
                filteredLocations.slice(0, 80).map((loc, i) => (
                  <div
                    key={i}
                    className={`booking-hub-qb-item ${i === selectedQuickBooking ? 'selected' : ''}`}
                    style={i === selectedQuickBooking ? { borderColor: hubDetails.colorCode || '#C9822A', backgroundColor: '#fcf6ef' } : {}}
                    onClick={() => setSelectedQuickBooking(i)}
                  >
                    <div className="booking-hub-qb-item-indicator" style={i === selectedQuickBooking ? { backgroundColor: hubDetails.colorCode || '#C9822A' } : {}}></div>
                    <div className="booking-hub-qb-item-text">
                      <div className="booking-hub-qb-item-name">{loc.landmark || loc.name}</div>
                      <div className="booking-hub-qb-item-landmark" style={i === selectedQuickBooking ? { color: hubDetails.colorCode || '#C9822A' } : {}}>{loc.name}</div>
                    </div>
                    {i === selectedQuickBooking && (
                      <CheckCircleOutlined className="booking-hub-qb-item-check" style={{ color: hubDetails.colorCode || '#C9822A' }} />
                    )}
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '24px 12px', color: '#888', fontSize: '12px' }}>
                  No sites match filters
                </div>
              )}
            </div>
            <button
              className="booking-hub-qb-book-btn"
              style={{ backgroundColor: hubDetails.colorCode || '#C9822A' }}
              disabled={filteredLocations.length === 0}
              onClick={() => {
                const selectedLoc = filteredLocations[selectedQuickBooking];
                if (selectedLoc) {
                  history.push(`/booking-hub/${gid}/location/${selectedLoc.gid}/next`);
                }
              }}
            >
              BOOK NOW
            </button>
          </div>
        )}

        <div className="booking-hub-filter-bar">
          <div className="booking-hub-filter-left">
            <div className="booking-hub-search-container">
              <SearchOutlined className="booking-hub-search-icon" />
              <input
                className="booking-hub-search-input"
                placeholder="Search sites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {allTags.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', overflow: 'hidden' }}>
                <div className="booking-hub-filter-icon" style={{ flexShrink: 0, marginRight: '4px' }}><FilterOutlined /></div>
                
                <button 
                  onClick={() => scrollTags('left')}
                  className="booking-hub-tags-scroll-btn booking-hub-tags-scroll-btn-left"
                >
                  <LeftOutlined style={{ fontSize: '10px' }} />
                </button>

                <div 
                  ref={tagsContainerRef}
                  className="booking-hub-tags-container" 
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', flexGrow: 1 }}
                >
                  {allTags.map((tag, index) => {
                    const isAllTag = tag.name === 'All';
                    const isActive = isAllTag ? activeTags.length === 0 : activeTags.includes(tag.name);
                    
                    const handleTagClick = () => {
                      if (isAllTag) {
                        setActiveTags([]);
                      } else {
                        if (activeTags.includes(tag.name)) {
                          setActiveTags(activeTags.filter(t => t !== tag.name));
                        } else {
                          setActiveTags([...activeTags, tag.name]);
                        }
                      }
                    };

                    return (
                      <div
                        className={`booking-hub-tag ${isActive ? 'active' : ''}`}
                        key={tag.gid || index}
                        onClick={handleTagClick}
                        style={isActive ? { backgroundColor: hubDetails.colorCode || '#C9822A', borderColor: hubDetails.colorCode || '#C9822A', color: '#fff' } : {}}
                      >
                        {tag.name}
                      </div>
                    );
                  })}
                  {(activeTags.length > 0 || searchQuery) && (
                    <div
                      className="booking-hub-tag clear-all-btn"
                      onClick={() => {
                        setActiveTags([]);
                        setSearchQuery('');
                      }}
                      style={{
                        backgroundColor: '#fee2e2',
                        borderColor: '#fca5a5',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        border: '1px solid',
                        fontSize: '11px',
                        marginLeft: '8px'
                      }}
                    >
                      Clear All
                    </div>
                  )}
                </div>

                <button 
                  onClick={() => scrollTags('right')}
                  className="booking-hub-tags-scroll-btn booking-hub-tags-scroll-btn-right"
                >
                  <RightOutlined style={{ fontSize: '10px' }} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {filteredLocations.length > 0 ? (
        <div className="booking-hub-grid">
          {filteredLocations.map((loc, index) => (
            <LocationCard key={loc.gid || index} loc={loc} gid={gid} />
          ))}
        </div>
      ) : (
        <div className="booking-hub-no-locations" style={{ textAlign: 'center', padding: '60px 20px', color: '#888', fontSize: '18px', fontWeight: '500' }}>
          No location found
        </div>
      )}
    </div>
  );
};

export default BookingHub;
